// @owners { team: patient-fulfillment }
import { Experimentation } from '@alto/experimentation';
import { nextAvailableDatesQueryKeys } from './nextAvailableDatesQueryKeys';
import { getScheduleablePrescriptionIDs } from '~shared/features/my-meds/selectors/getScheduleablePrescriptions';
import { getNextAvailableDateAddressID } from '~shared/features/next-available-date/selectors/getNextAvailableDateAddressId';
import { getNextAvailableDateFacilityID } from '~shared/features/next-available-date/selectors/getNextAvailableDateFacilityId';
import { getNextAvailableDates } from '~shared/features/next-available-date/selectors/getNextAvailableDates';
import { getNextAvailableDatesByPrescriptionID } from '~shared/features/next-available-date/selectors/getNextAvailableDatesByPrescriptionID';
import { get } from '~shared/helpers/apiHelper';
import { staleTimes } from '~shared/queries/constants';
import { useQueryClient } from '~shared/react-query';
import { type NextAvailableDateMapping } from '~shared/reducers/nextAvailableDates';
import { useSelectorShared } from '~shared/store';
import { type NextAvailableDate, type NextAvailableDateResponse } from '~shared/types';

type AllNextAvailableDatesParams = {
  addressID?: number;
  facilityID?: number;
  prescriptionIDs: number[];
  shipmentID?: number;
};

const fetchAllNextAvailableDates = async (
  params: AllNextAvailableDatesParams,
): Promise<NextAvailableDateResponse[]> => {
  const requestParams = {
    address_id: params.addressID,
    facility_id: params.facilityID,
    shipment_id: params.shipmentID,
    unbundled: params.prescriptionIDs.join(','),
  };

  return get('/next_available_dates', requestParams, { version: 'v3' }).then((response) => {
    if (response.error) throw new Error(response.error);
    return response;
  });
};

export const usePrefetchAllNextAvailableDates = () => {
  const { value: showNewMedList } = Experimentation.useFeatureFlag('new_med_details_and_med_list');
  const queryClient = useQueryClient();
  const addressID = useSelectorShared(getNextAvailableDateAddressID);
  const facilityID = useSelectorShared(getNextAvailableDateFacilityID);
  const prescriptionIDs = useSelectorShared(getScheduleablePrescriptionIDs);

  const prefetchAllNextAvailableDates = async () => {
    if (!prescriptionIDs.length) return;
    if (showNewMedList) return;

    await queryClient.prefetchQuery<NextAvailableDateResponse[]>({
      queryKey: nextAvailableDatesQueryKeys.forPrescriptions({ prescriptionIDs, addressID, facilityID }),
      queryFn: () => {
        if (!prescriptionIDs) throw new Error('query should not run without a prescriptionID');

        return fetchAllNextAvailableDates({ prescriptionIDs, addressID, facilityID });
      },
      staleTime: staleTimes.fiveMinutes,
    });
  };

  return { prefetchAllNextAvailableDates };
};

type NextAvailableDatesForPrescriptionsParams = {
  addressID?: number;
  facilityID?: number;
  prescriptionID?: number;
  shipmentID?: number;
};

type NextAvailableDatesForPrescriptionsResponse = {
  error?: any;
  isError: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  nextAvailableDate: NextAvailableDate | null;
  nextAvailableDates?: NextAvailableDateMapping;
  nextAvailableDatesByPrescriptionID: Record<number, NextAvailableDate>;
};

export const useNextAvailableDatesForPrescriptions = (
  params: NextAvailableDatesForPrescriptionsParams = {},
): NextAvailableDatesForPrescriptionsResponse => {
  const { prescriptionID } = params;
  const nextAvailableDatesByPrescriptionID = useSelectorShared(getNextAvailableDatesByPrescriptionID);
  const nextAvailableDates = useSelectorShared(getNextAvailableDates);

  return {
    isError: false,
    isLoading: false,
    isSuccess: true,
    nextAvailableDate: prescriptionID ? nextAvailableDatesByPrescriptionID[prescriptionID] : null,
    nextAvailableDates,
    nextAvailableDatesByPrescriptionID,
  };
};
